import { useEffect } from 'react'
import { useQuote } from '@/api/queries'
import { StatusBadge } from '@/components/ui/Badge'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Detail, DetailLink } from '@/components/review/DetailList'
import { ReviewDrawer } from '@/components/review/ReviewDrawer'
import { useEntityDraft } from '@/components/review/useEntityDraft'
import { refLabel } from '@/features/programs/columns'
import type { DateProvenance, Quote } from '@/types'

/** The attribution fields, as the page wrote them. Blank saves as null. */
const ATTRIBUTION = ['speakerName', 'classYear', 'majors', 'currentPosition', 'hometown'] as const

interface Draft {
  text: string
  speakerName: string
  classYear: string
  majors: string
  currentPosition: string
  hometown: string
  tags: string[]
}

const DEFAULTS: Draft = {
  text: '',
  speakerName: '',
  classYear: '',
  majors: '',
  currentPosition: '',
  hometown: '',
  tags: [],
}

const PROVENANCE_LABELS: Record<DateProvenance, string> = {
  explicit: 'stated on the page',
  grad_year: 'from the class year',
  url_segment: 'from the page URL',
  last_modified: 'page last modified',
}

/**
 * Editing before approving is the core action here — extracted attribution is
 * frequently wrong. The text and speaker fields are editable in place rather
 * than behind an Edit button, and that must survive any refactor
 * (docs/03-page-specs.md §6.2). The source page and the context are the
 * crawl's and are shown, not offered as fields.
 */
export function QuoteDrawer({
  clientId,
  quote,
  tagPool,
  onClose,
  onSave,
  onReview,
  onFavorite,
}: {
  clientId: string
  quote: Quote | 'new' | null
  tagPool: string[]
  onClose: () => void
  onSave: (patch: Partial<Quote> & { id?: string }) => Promise<void>
  onReview: (id: string, status: 'approved' | 'rejected') => void
  onFavorite: (id: string, favorite: boolean) => void
}) {
  const creating = quote === 'new'
  const existing = creating ? null : quote
  const { data: detail } = useQuote(clientId, existing?.id ?? null)

  const draft = useEntityDraft<Draft>(
    existing
      ? {
          text: existing.text,
          speakerName: existing.speakerName ?? '',
          classYear: existing.classYear ?? '',
          majors: existing.majors ?? '',
          currentPosition: existing.currentPosition ?? '',
          hometown: existing.hometown ?? '',
          tags: existing.tags,
        }
      : null,
    DEFAULTS,
  )

  const { reset } = draft
  useEffect(() => {
    if (!quote) reset()
  }, [quote, reset])

  if (!quote) return null

  function close() {
    draft.reset()
    onClose()
  }

  function save() {
    const { text, tags } = draft.value
    const attribution = Object.fromEntries(ATTRIBUTION.map((k) => [k, draft.value[k].trim() || null]))
    void onSave({ ...(existing ? { id: existing.id } : {}), text, tags, ...attribution })
  }

  const attributionField = (key: (typeof ATTRIBUTION)[number], label: string, hint?: string) => (
    <Field label={label} hint={hint}>
      {(p) => <Input {...p} value={draft.value[key]} onChange={(e) => draft.set(key, e.target.value)} />}
    </Field>
  )

  return (
    <ReviewDrawer
      open
      onClose={close}
      width="w-[620px]"
      title={creating ? 'Add quote' : 'Review quote'}
      subtitle={existing && <StatusBadge status={existing.status} />}
      creating={creating}
      entity={existing}
      saveLabel={creating ? 'Add quote' : 'Save changes'}
      saveDisabled={!draft.value.text.trim()}
      onSave={save}
      onApprove={() => existing && onReview(existing.id, 'approved')}
      onReject={() => existing && onReview(existing.id, 'rejected')}
      onFavorite={(next) => existing && onFavorite(existing.id, next)}
      tags={draft.value.tags}
      tagPool={tagPool}
      onTagsChange={(tags) => draft.set('tags', tags)}
    >
      <Field label="Quote" required hint="Edit for accuracy before approving. Keep the speaker's wording.">
        {(p) => (
          <Textarea
            {...p}
            rows={4}
            value={draft.value.text}
            className="text-[15px] leading-7"
            onChange={(e) => draft.set('text', e.target.value)}
          />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-4">
        {attributionField('speakerName', 'Speaker name')}
        {attributionField('classYear', 'Class year', "As written: ’27, Class of 2021, Alumna")}
        {attributionField('majors', 'Majors')}
        {attributionField('currentPosition', 'Current position', 'Job, or faculty or parent role')}
        {attributionField('hometown', 'Hometown')}
      </div>

      {existing && (
        <dl className="space-y-2 border-t border-rule pt-4">
          <Detail label="Source page">{existing.sourceUrl && <DetailLink url={existing.sourceUrl} />}</Detail>
          <Detail label="Date">
            {existing.quoteDate &&
              `${existing.quoteDate}${existing.dateProvenance ? `, ${PROVENANCE_LABELS[existing.dateProvenance]}` : ''}`}
          </Detail>
          <Detail label="Mentions">{detail?.majorMinorMentioned}</Detail>
          <Detail label="Programs">
            {detail && detail.programs.length > 0 ? detail.programs.map(refLabel).join(', ') : null}
          </Detail>
          {existing.context && (
            <Detail label="Context">
              <p className="rounded-[4px] bg-canvas px-3 py-2.5 leading-6 text-ink-muted">{existing.context}</p>
            </Detail>
          )}
          {detail?.notes && <Detail label="Notes">{detail.notes}</Detail>}
        </dl>
      )}
    </ReviewDrawer>
  )
}
